import { Button, HStack, Image, Stack, Text } from "@chakra-ui/react";
import useLocale from "@raiden/library/hooks/useLocale";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage } from "react-intl";
import RatingStars from "../StarRate";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

// en locale works but not fr for some reason. So we override en locale with fr translations.
dayjs.updateLocale("en", {
  relativeTime: {
    past: "Il y a %s",
    s: "quelques secondes",
    m: "Une minute",
    mm: "%d minutes",
    h: "une heure",
    hh: "%d heures",
    d: "un jour",
    dd: "%d jours",
    M: "un mois",
    MM: "%d mois",
    y: "un an",
    yy: "%d ans",
  },
});

/**
 * @param {object} param0
 * @param {string} param0.locale
 * @param {import("@raiden/library/types/Post").Post} param0.review
 * @returns {number|null}
 */
function getRateFromReview({ review, locale }) {
  if (
    !review ||
    !review.current_revision ||
    !review.current_revision.keywords
  ) {
    return null;
  }

  const keywords = review.current_revision.keywords[locale];
  const match = keywords.match(/rate:(\d+(?:\.\d+)?)/);

  if (!match) {
    return null;
  }

  const rate = parseFloat(match[1]);
  return isNaN(rate) ? null : rate;
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library/types/Post").Post} review
 * @property {import("react").Dispatch<React.SetStateAction<import("@raiden/library/types/Post").Post | null>>} setSelectedReview
 */

/**
 * @param {Props} props
 */

export function Review({ review, setSelectedReview }) {
  const translate = useTranslate();

  const { locale } = useLocale();

  const rate = getRateFromReview({ review, locale });

  const correctRate = rate && rate > 5 ? 5 : rate;

  const shouldRenderMoreButton =
    (translate(review.current_revision?.summary)?.length ?? 0) > 240;

  return (
    <Stack spacing=".5rem">
      <HStack justifyContent="space-between">
        <Text variant="text-label" color="brandPrimary.900">
          {translate(review.current_revision?.title)}
        </Text>

        <Image
          src={generateFrontPath({
            id: "@front.internal-assets",
            parameters: { filePath: "google.png" },
            includeBasePath: true,
          })}
          alt=""
          maxW="24px"
          maxH="24px"
        />
      </HStack>

      {correctRate && <RatingStars rate={correctRate} />}

      <Text variant="text-small" color="brandSecondary.600" noOfLines={4}>
        {translate(review.current_revision?.summary)}
      </Text>

      <HStack justifyContent="space-between">
        <Text variant="text-small" color="brandSecondary.600">
          {dayjs(review.publication_date).fromNow()}
        </Text>

        {shouldRenderMoreButton && (
          <Button
            variant="unstyled"
            color="brandSecondary.600"
            size="sm"
            onClick={() => setSelectedReview(review)}>
            <FormattedMessage defaultMessage="Voir plus" />
          </Button>
        )}
      </HStack>
    </Stack>
  );
}
